<template>
  <div class="detail-score-panel" :class="{ 'is-readonly': readonly }">
    <div class="subject-nav">
      <button
        v-for="subject in subjects"
        :key="subject.key"
        type="button"
        class="subject-nav-item"
        :class="{ active: activeKey === subject.key }"
        @click="activeKey = subject.key"
      >
        <span>{{ subject.name }}</span>
        <strong>{{ subjectTotal(subject.key) }}分</strong>
      </button>
    </div>
    <div class="subject-content">
      <div class="subject-header">
        <div>
          <div class="subject-name">{{ activeSubject.name }}</div>
          <div class="subject-count">共 {{ activeSubject.count }} 题，每题 0-3 分</div>
        </div>
        <div class="subject-total">合计 <strong>{{ subjectTotal(activeKey) }}</strong> 分</div>
      </div>
      <div class="question-grid">
        <div
          v-for="item in columnItems"
          :key="item.number"
          class="question-item"
          :style="{ gridColumn: String(item.column), gridRow: String(item.row) }"
        >
          <span class="question-number">第 {{ item.number }} 题</span>
          <span v-if="readonly" class="readonly-score">{{ questionValue(activeKey, item.number - 1) }}</span>
          <a-radio-group
            v-else
            :value="questionValue(activeKey, item.number - 1)"
            button-style="solid"
            size="small"
            @change="changeScore(item.number - 1, $event.target.value)"
          >
            <a-radio-button v-for="option in scoreOptions" :key="option" :value="option">
              {{ option }}
            </a-radio-button>
          </a-radio-group>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const ABAS_INFANT_TEACHER_SUBJECTS = [
  {key: 'communicate', name: '沟通', count: 21},
  {key: 'study', name: '学前功能', count: 22},
  {key: 'family', name: '学校生活', count: 22},
  {key: 'healthy', name: '健康与安全', count: 19},
  {key: 'leisure', name: '休闲', count: 20},
  {key: 'self_care', name: '自我照顾', count: 21},
  {key: 'self_guidance', name: '自我管理', count: 19},
  {key: 'social', name: '社交', count: 22},
  {key: 'skills', name: '动作技巧', count: 24},
];

const ABAS_INFANT_PARENT_SUBJECTS = [
  {key: 'communicate', name: '沟通', count: 23},
  {key: 'community', name: '社区应用', count: 22},
  {key: 'study', name: '学前功能', count: 24},
  {key: 'family', name: '居家生活', count: 24},
  {key: 'healthy', name: '健康与安全', count: 23},
  {key: 'leisure', name: '休闲', count: 22},
  {key: 'self_care', name: '自我照顾', count: 21},
  {key: 'self_guidance', name: '自我管理', count: 21},
  {key: 'social', name: '社交', count: 21},
  {key: 'skills', name: '动作技巧', count: 24},
];

const ABAS_CHILD_TEACHER_SUBJECTS = [
  {key: 'communicate', name: '沟通', count: 23},
  {key: 'community', name: '社区应用', count: 19},
  {key: 'study', name: '学前功能', count: 24},
  {key: 'family', name: '学校生活', count: 23},
  {key: 'healthy', name: '健康与安全', count: 21},
  {key: 'leisure', name: '休闲', count: 22},
  {key: 'self_care', name: '自我照顾', count: 17},
  {key: 'self_guidance', name: '自我管理', count: 23},
  {key: 'social', name: '社交', count: 23},
];

const ABAS_CHILD_PARENT_SUBJECTS = [
  {key: 'communicate', name: '沟通', count: 23},
  {key: 'community', name: '社区应用', count: 24},
  {key: 'study', name: '学习功能', count: 23},
  {key: 'family', name: '居家生活', count: 24},
  {key: 'healthy', name: '健康与安全', count: 24},
  {key: 'leisure', name: '休闲', count: 24},
  {key: 'self_care', name: '自我照顾', count: 24},
  {key: 'self_guidance', name: '自我管理', count: 24},
  {key: 'social', name: '社交', count: 23},
];

const ABAS_ADULT_SUBJECTS = [
  {key: 'communicate', name: '沟通', count: 24},
  {key: 'community', name: '社区应用', count: 24},
  {key: 'study', name: '学习功能', count: 24},
  {key: 'family', name: '居家生活', count: 24},
  {key: 'healthy', name: '健康与安全', count: 24},
  {key: 'leisure', name: '休闲', count: 22},
  {key: 'self_care', name: '自我照顾', count: 22},
  {key: 'self_guidance', name: '自我管理', count: 24},
  {key: 'social', name: '社交', count: 23},
  {key: 'work', name: '工作', count: 23},
];

export default {
  name: 'AbasScoreDetail',
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    scoringType: {
      type: String,
      default: '2',
    },
    edition: {
      type: String,
      default: '1',
    },
  },
  data() {
    return {
      activeKey: 'communicate',
      scoreOptions: [0, 1, 2, 3],
    };
  },
  computed: {
    subjects() {
      if (this.edition === '3') return ABAS_ADULT_SUBJECTS;
      if (this.edition === '2' && this.scoringType === '1') return ABAS_CHILD_PARENT_SUBJECTS;
      if (this.edition === '2' && this.scoringType === '2') return ABAS_CHILD_TEACHER_SUBJECTS;
      return this.scoringType === '1' ? ABAS_INFANT_PARENT_SUBJECTS : ABAS_INFANT_TEACHER_SUBJECTS;
    },
    activeSubject() {
      return this.subjects.find((subject) => subject.key === this.activeKey) || this.subjects[0];
    },
    // 按列从上到下排序：左列第 1~N 题，右列第 N+1~最后，readonly 三列同理
    columnItems() {
      const count = this.activeSubject.count;
      const columns = this.readonly ? 3 : 2;
      const rows = Math.ceil(count / columns);
      return Array.from({length: count}, (_, index) => ({
        number: index + 1,
        column: Math.floor(index / rows) + 1,
        row: (index % rows) + 1,
      }));
    },
  },
  watch: {
    subjects: {
      handler(subjects) {
        if (!subjects.some((subject) => subject.key === this.activeKey)) {
          this.activeKey = subjects[0].key;
        }
      },
      immediate: true,
    },
    value: {
      handler() {
        this.$emit('totals-change', this.calculateTotals(this.normalizedScores()));
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    if (!this.readonly) this.$emit('input', this.normalizedScores());
  },
  methods: {
    normalizedScores() {
      const normalized = {};
      this.subjects.forEach((subject) => {
        const values = Array.isArray(this.value && this.value[subject.key]) ? this.value[subject.key] : [];
        normalized[subject.key] = Array.from({length: subject.count}, (_, index) => {
          const score = Number(values[index]);
          return this.scoreOptions.includes(score) ? score : 0;
        });
      });
      return normalized;
    },
    questionValue(subject, index) {
      return this.normalizedScores()[subject][index];
    },
    changeScore(index, value) {
      const scores = this.normalizedScores();
      this.$set(scores[this.activeKey], index, Number(value));
      this.$emit('input', scores);
      this.$emit('totals-change', this.calculateTotals(scores));
    },
    subjectTotal(subject) {
      return this.normalizedScores()[subject].reduce((total, score) => total + score, 0);
    },
    calculateTotals(scores) {
      return this.subjects.reduce((totals, subject) => {
        totals[`score_${subject.key}`] = scores[subject.key].reduce((total, score) => total + score, 0);
        return totals;
      }, {});
    },
  },
};
</script>

<style scoped>
.detail-score-panel {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  min-height: 360px;
  margin: 24px 0 34px;
  overflow: hidden;
  border: 1px solid #eadfea;
  border-radius: 6px;
  background: #fff;
}
.subject-nav {
  padding: 10px;
  border-right: 1px solid #eadfea;
  background: #faf7fa;
}
.subject-nav-item {
  display: flex;
  width: 100%;
  min-height: 42px;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  border: 0;
  border-radius: 4px;
  color: #555;
  background: transparent;
  cursor: pointer;
}
.subject-nav-item strong {
  color: #999;
  font-size: 12px;
}
.subject-nav-item.active {
  color: #832c75;
  background: #f1e7f0;
  font-weight: 600;
}
.subject-nav-item.active strong {
  color: #832c75;
}
.subject-content {
  min-width: 0;
  padding: 20px 22px 24px;
}
.subject-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
}
.subject-name {
  color: #333;
  font-size: 16px;
  font-weight: 600;
}
.subject-count {
  margin-top: 3px;
  color: #999;
  font-size: 12px;
}
.subject-total {
  color: #666;
}
.subject-total strong {
  color: #832c75;
  font-size: 20px;
}
.question-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(250px, 1fr));
  gap: 10px 18px;
  padding-top: 18px;
}
.detail-score-panel.is-readonly .question-grid {
  grid-template-columns: repeat(3, minmax(160px, 1fr));
}
.question-item {
  display: flex;
  min-height: 38px;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px 4px 12px;
  border-bottom: 1px solid #f1f1f1;
}
.question-number {
  color: #666;
  font-size: 13px;
}
.readonly-score {
  width: 34px;
  height: 28px;
  border-radius: 4px;
  color: #832c75;
  background: #f1e7f0;
  font-weight: 600;
  line-height: 28px;
  text-align: center;
}
@media (max-width: 900px) {
  .detail-score-panel {
    grid-template-columns: 1fr;
  }
  .subject-nav {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    border-right: 0;
    border-bottom: 1px solid #eadfea;
  }
  .question-grid {
    grid-template-columns: 1fr;
  }
  .detail-score-panel.is-readonly .question-grid {
    grid-template-columns: repeat(2, minmax(160px, 1fr));
  }
}
@media (max-width: 560px) {
  .subject-nav {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .detail-score-panel.is-readonly .question-grid {
    grid-template-columns: 1fr;
  }
}
</style>
